import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ActivatedRoute, Params } from '@angular/router';
import { ParamsService } from '@callrail/looky/util';
import { BehaviorSubject, combineLatest, Observable, of } from 'rxjs';
import {
  catchError,
  distinctUntilChanged,
  filter,
  map,
  shareReplay,
  startWith,
  switchMap,
  take,
} from 'rxjs/operators';

import { AgencyModel } from '../../models/agency.model';
import { AppNotice } from '../../models/app-notice.model';
import { CompanyModel } from '../../models/company.model';
import { UserModel } from '../../models/user.model';
import { CurrentContextService } from '../../services/current-context/current-context.service';
import { ErrorReporterService } from '../error-reporter/error-reporter.service';

@Injectable({
  providedIn: 'root',
})
export class AppNoticesService {
  private dismissedNoticeIds$ = new BehaviorSubject<Set<number | string>>(
    new Set()
  );

  // reload when the agency gets reloaded
  public appNotices$: Observable<AppNotice[]> = combineLatest([
    this.contextData.pipe(
      filter(() => !!this.agency),
      switchMap(() => this.fetchAppNotices()),
      startWith(null) // emit an initial value of null
    ),
    this.dismissedNoticeIds$,
  ]).pipe(
    map(([appNotices, dismissedNoticeIds]) =>
      appNotices
        ? appNotices.filter((notice) => !dismissedNoticeIds.has(notice.id))
        : appNotices
    ),
    shareReplay(1)
  );

  public appAlertsUrl$: Observable<string> = this.route.queryParams.pipe(
    map((params: Params) => this.appAlertsUrl(params))
  );

  public status$ = new BehaviorSubject<string[]>([]);
  public unreadAppNoticeCount$: Observable<number> = this.status$.pipe(
    map((noticeIds: string[]) => {
      const viewedAppNotices =
        (JSON.parse(localStorage.getItem('viewedAppNotices')) as string[]) ||
        [];
      return noticeIds.filter(
        (noticeId) => !viewedAppNotices.includes(noticeId)
      ).length;
    })
  );

  private agency: AgencyModel;
  private company: CompanyModel;
  private user: UserModel;
  private companyIds: string[];
  private localStoreLock = false;

  constructor(
    private context: CurrentContextService,
    private http: HttpClient,
    private paramsService: ParamsService,
    private route: ActivatedRoute,
    private errorReporterService: ErrorReporterService
  ) {
    this.contextData.subscribe(() => {
      if (this.agency) {
        this.fetchStatus();
      }
    });
  }

  private get contextData(): Observable<void> {
    return combineLatest([
      this.context.agency,
      this.context.company,
      this.context.user,
      this.route.queryParams,
    ]).pipe(
      distinctUntilChanged(
        ([agencyA, companyA, userA], [agencyB, companyB, userB]) =>
          agencyA?.id === agencyB?.id &&
          companyA?.id === companyB?.id &&
          userA?.id === userB?.id
      ),
      map(([agency, company, user, queryParams]) => {
        this.agency = agency;
        this.company = company;
        this.companyIds = queryParams?.company_id?.split(',');
        this.user = user;
      })
    );
  }

  public markViewed(notice_id: number | string) {
    return this.http
      .post(this.appNoticesUrl('/app-notices/viewed.json'), { notice_id })
      .subscribe();
  }

  public dismissAgencyUserAlert(id: number | string) {
    const dismissedNoticeIds = new Set(this.dismissedNoticeIds$.value);
    dismissedNoticeIds.add(id);
    this.dismissedNoticeIds$.next(dismissedNoticeIds);

    return this.http
      .post(`/a/${this.agency?.id}/user_alerts/${id}/dismiss.json`, {})
      .pipe(
        catchError((error) => {
          this.errorReporterService.report(error, {
            id,
            agencyId: this.agency?.id,
          });
          const rolledBackNoticeIds = new Set(this.dismissedNoticeIds$.value);
          rolledBackNoticeIds.delete(id);
          this.dismissedNoticeIds$.next(rolledBackNoticeIds);
          return of(null);
        })
      )
      .subscribe();
  }

  public getViewedLocalStore(): string[] {
    return JSON.parse(localStorage.getItem('viewedAppNotices')) || [];
  }

  public getUnreadCountLocalStore(
    noticeIds: string[],
    agencyId: string
  ): number {
    const viewed = this.getNoticeIdsLocalStore(agencyId, null);
    let singleInstallNotice = false;
    const filteredNoticeIds = (noticeIds || []).filter((id: string) => {
      // We only ever display one "js_not_installed" notification at a time in the UI, even if
      // multiple companies have the notification. Users get confused when this count doesn't
      // match the number of visible notifications.
      const isInstallNotice = id.match('js_not_installed');
      if (isInstallNotice && !singleInstallNotice) {
        singleInstallNotice = true;
      } else if (isInstallNotice && singleInstallNotice) {
        return false;
      }
      // Return all unseen notice ids
      return !viewed.includes(id);
    });
    return filteredNoticeIds.length;
  }

  // we want to keep the already viewed notices in the array so we can track
  // notice views accross accounts. We can filter out the current agency's ids
  // to keep them current when we combine the viewedNoticeIds with hte storedNoticesIds
  public markViewedLocalStore(appNotices: AppNotice[]) {
    combineLatest([this.context.agency, this.context.company])
      .pipe(take(1))
      .subscribe(([agency, company]) => {
        while (this.localStoreLock) {
          setTimeout(() => {
            return;
          }, 100);
        }
        let noticeIds: string[];
        this.localStoreLock = true;
        try {
          noticeIds = this.mergeViewedIds(agency, company, appNotices);
          localStorage.setItem('viewedAppNotices', JSON.stringify(noticeIds));
        } finally {
          this.localStoreLock = false;
        }

        this.status$.next(noticeIds);
      });
  }

  private mergeViewedIds(agency, company, appNotices) {
    // A set ensures we don't add duplicate ids
    const noticeIds = new Set(
      this.getNoticeIdsLocalStore(agency?.id, company?.id)
    );
    if (Array.isArray(appNotices)) {
      for (const notice of appNotices) {
        // append viewed notices name + agency id to local storage item + notice.id
        const noticeId = `${notice.name}${agency.id}${notice.id || ''}`;
        noticeIds.add(noticeId);
      }
    }
    return Array.from(noticeIds);
  }

  /**
   * pull stored notice ids and filter out the noticeIds for the current agency
   * in order to reset the ids we have in localStorage. This allows us to keep
   * localStorage up to date with only the relavent noticeIds across agencies
   *
   * we check if the id is null because we have seen some honeybadgers around null ids.
   *
   * js_not_installed is a company level notice so we need to keep these notices accounted for
   * in localStorage per company, we are attaching the agency.masked_id + company.masked_id to these notices.
   */
  private getNoticeIdsLocalStore(agencyId, companyId) {
    return this.getViewedLocalStore().filter((id: string) => {
      try {
        if (companyId && id.match('js_not_installed')) {
          // filter company scoped noticeIds
          return !id.match(`${agencyId}${companyId}`);
        } else {
          // filter agency scoped noticeIds
          return !id.match(agencyId);
        }
      } catch (error) {
        return false;
      }
    });
  }

  private fetchStatus() {
    this.http
      .get(this.appNoticesUrl('/app-notices/status.json'))
      .subscribe((resp: string[]) => this.status$.next(resp));
  }

  private fetchAppNotices(): Observable<AppNotice[]> {
    return this.http.get<AppNotice[]>(this.appNoticesUrl('/app-notices.json'));
  }

  private appNoticesUrl(endpoint: string): string {
    return this.paramsService.appendToString(endpoint, {
      agency_id: this.agency?.id,
      user_id: this.user?.id,
      company_ids: this.company ? this.companyIds || this.company.id : null,
    });
  }

  private appAlertsUrl(params: Params): string {
    const companyId = params.company_id;
    const companyIdParam =
      companyId && companyId !== '' ? `?company_id=${companyId}` : '';
    return `/settings/a/${this.agency?.id}/account/app-alerts${companyIdParam}`;
  }
}
